import { api, request, requireAccount, withAccount } from "./core";

// GalaxyMember is an auth user who can log in to this galaxy (account
// membership), used by the chronicle's member-note picker to set target_user_id.
// Distinct from observation members (git authors) in team-health.
export interface GalaxyMember {
  user_id: number;
  login: string;
  avatar_url: string;
  role: string;
  departed_at?: string; // RFC3339; set = this member has left the galaxy
}

export const membersApi = {
  // Departure is observation-driven (the GitHub org member set), so there is no
  // manual depart/restore call — list is read-only here.
  list: (accountLogin: string) =>
    api.get<GalaxyMember[]>(withAccount("/members", requireAccount(accountLogin, "membersApi.list"))),
};

// Team is a department (parent_team_id null) or a team (parent_team_id set to a
// department) within a galaxy. The department/team tree scopes galaxy views for
// large orgs. source tells where it came from; a 'suggested' team is hidden from
// the switcher until approved_at is set.
export interface Team {
  id: number;
  account_id: number;
  parent_team_id?: number | null;
  name: string;
  source: "manual" | "github_sync" | "suggested";
  approved_at?: string | null;
  created_at: string;
  updated_at: string;
}

// TeamMembershipView maps a team to the member GitHub identities (ex_github_id)
// currently in it (active only). The FE resolves ex_github_id → login/avatar.
export interface TeamMembershipView {
  team_id: number;
  ex_github_ids: number[];
}

// TeamSyncResult summarizes a GitHub Teams sync for the UI toast.
export interface TeamSyncResult {
  departments: number;
  teams: number;
  members: number;
}

// teamApi manages the department/team tree + memberships. Members are keyed by
// GitHub identity (ex_github_id == observation github_user_id), so an observed
// git author who never logged into Ace is still a member. Every call carries
// account_login (galaxy scope, D-03). Reads are open to members; writes admin-only.
// Review Flow (FLOW/ACTIVITY): aggregated PR-review counts, NOT gravity. Read
// as monthly totals by state + the most active reviewers; never per-review rows.
export type MonthlyReviewCount = {
  month: string; // YYYY-MM
  approved: number;
  changes_requested: number;
  commented: number;
  total: number;
};
export type ReviewerCount = { reviewer: string; count: number };
export type ReviewFlowSummary = { months: MonthlyReviewCount[]; top_reviewers: ReviewerCount[] };

// TeamSuggestion is one pending data-driven team proposal: an auto-derived name
// and the member GitHub identities who co-own the same modules.
export interface TeamSuggestion {
  team_id: number;
  name: string;
  ex_github_ids: number[];
}

export const teamApi = {
  tree: (accountLogin: string) =>
    api.get<Team[]>(withAccount("/teams", requireAccount(accountLogin, "teamApi.tree"))),
  reviewFlow: (accountLogin: string) =>
    api.get<ReviewFlowSummary>(withAccount("/teams/review-flow", requireAccount(accountLogin, "teamApi.reviewFlow"))),
  memberships: (accountLogin: string) =>
    api.get<TeamMembershipView[]>(withAccount("/teams/memberships", requireAccount(accountLogin, "teamApi.memberships"))),
  create: (accountLogin: string, data: { name: string; parent_team_id?: number | null }) =>
    api.post<Team>(withAccount("/teams", requireAccount(accountLogin, "teamApi.create")), data),
  update: (accountLogin: string, id: number, data: { name: string; parent_team_id?: number | null }) =>
    request<void>(withAccount(`/teams/${id}`, requireAccount(accountLogin, "teamApi.update")), {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  remove: (accountLogin: string, id: number) =>
    request<void>(withAccount(`/teams/${id}`, requireAccount(accountLogin, "teamApi.remove")), {
      method: "DELETE",
    }),
  addMember: (accountLogin: string, teamId: number, exGithubId: number) =>
    api.post<void>(withAccount("/teams/members", requireAccount(accountLogin, "teamApi.addMember")), {
      team_id: teamId,
      ex_github_id: exGithubId,
    }),
  removeMember: (accountLogin: string, teamId: number, exGithubId: number) =>
    request<void>(withAccount("/teams/members", requireAccount(accountLogin, "teamApi.removeMember")), {
      method: "DELETE",
      body: JSON.stringify({ team_id: teamId, ex_github_id: exGithubId }),
    }),
  // Mint a shareable, collective-anonymous team-orbit card token (any member). The
  // token carries only the team's aggregate (no individuals); the public-api host
  // renders the noindex /team/:token landing + /card/.../org image with no DB read.
  // A 409 "team_too_small" comes back when the team has < 5 observed contributors.
  orbitCard: (accountLogin: string, teamId: number) =>
    api.post<{ token: string; path: string; card_path: string }>(
      withAccount(`/teams/${teamId}/orbit-card`, requireAccount(accountLogin, "teamApi.orbitCard")),
      {},
    ),
  syncGithub: (accountLogin: string) =>
    api.post<TeamSyncResult>(withAccount("/teams/sync-github", requireAccount(accountLogin, "teamApi.syncGithub")), {}),
  // Data-driven team suggestions (module co-ownership clustering). generate
  // (re)computes them; suggestions lists the pending ones; approve promotes one
  // into the live tree. Reject is just remove() (a suggestion is an unapproved team).
  suggestions: (accountLogin: string) =>
    api.get<TeamSuggestion[]>(withAccount("/teams/suggestions", requireAccount(accountLogin, "teamApi.suggestions"))),
  generateSuggestions: (accountLogin: string) =>
    api.post<TeamSuggestion[]>(withAccount("/teams/suggestions/generate", requireAccount(accountLogin, "teamApi.generateSuggestions")), {}),
  approveSuggestion: (accountLogin: string, id: number) =>
    api.post<void>(withAccount(`/teams/suggestions/${id}/approve`, requireAccount(accountLogin, "teamApi.approveSuggestion")), {}),
};

// projectHealthApi mints a shareable, collective-anonymous PROJECT-HEALTH card token
// for the whole galaxy (any member). The token carries only the project's aggregate
// sustainability scalars (bus-factor / Orphaned Gravity / danger modules / collapse —
// no individuals); the public-api host renders the INDEXABLE /health/:token landing
// with no DB read. A 409 "project_too_small" comes back when the project has < 5
// observed contributors. Galaxy scope on the path (account_login, D-03).
export const projectHealthApi = {
  card: (accountLogin: string) =>
    api.post<{ token: string; path: string }>(
      withAccount("/galaxy/health-card", requireAccount(accountLogin, "projectHealthApi.card")),
      {},
    ),
};
